import React from "react";
import { Modal, Button } from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import { useTranslation } from "react-i18next";
import "./generic-modal.style.scss";

interface GenericModalProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: "sm" | "lg" | "xl" ;
  showFooter?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  closeOnBackdrop?: boolean;
  showCloseButton?: boolean;
  variant?: "default" | "centered" | "fullwidth";
  className?: string;
}

const GenericModal: React.FC<GenericModalProps> = ({
  show,
  onClose,
  title,
  children,
  size = "xl",
  showFooter = true,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction,
  closeOnBackdrop = true,
  showCloseButton = true,
  variant = "default",
  className = "",
}) => {
  const { t } = useTranslation();

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalClasses = [
    "generic-modal",
    `generic-modal--${variant}`,
    className
  ].filter(Boolean).join(" ");

  return (
    <Modal
      show={show}
      onHide={closeOnBackdrop ? onClose : undefined}
      size={size}
      centered={variant === "centered"}
      className={modalClasses}
      backdrop={closeOnBackdrop ? true : "static"}
      animation={false}
    >
      {(title || showCloseButton) && (
        <Modal.Header className="generic-modal__header">
          {title && (
            <Modal.Title className="generic-modal__title">
              {title}
            </Modal.Title>
          )}
          {showCloseButton && (
            <button
              type="button"
              className="generic-modal__close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <AiOutlineClose size={24} />
            </button>
          )}
        </Modal.Header>
      )}

      <Modal.Body className="generic-modal__body">
        {children}
      </Modal.Body>

      {showFooter && (
        <Modal.Footer className="generic-modal__footer">
          {secondaryButtonText && (
            <Button
              variant="outline-primary"
              onClick={onSecondaryAction || onClose}
              className="generic-modal__secondary-btn"
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              variant="primary"
              onClick={onPrimaryAction}
              className="generic-modal__primary-btn"
            >
              {primaryButtonText}
            </Button>
          )}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default GenericModal;
